import { Outlet, NavLink, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import PageLayout from '../../components/layout/page-layout'
import { Container } from '../../components/ui/container'
import { pagePanel } from '../../components/layout/page-styles'
import { userService } from '../../services'
import { useLocale } from '../../hooks/use-locale'
import { Badge } from '../../components/ui/badge'
import { FEATURES } from '../../config/features.config'
import { FileText, Clock, User, MapPin, Phone, FolderOpen, MessageSquare } from 'lucide-react'

export default function UserDetailLayout() {
  const { t } = useTranslation()
  const { formatDate } = useLocale()
  const { id } = useParams()

  const navItems = [
    { to: 'personnel-file', label: t('users.navPersonnelFile'), icon: FileText },
    ...(FEATURES.timeTracking
      ? [{ to: 'time-tracking', label: t('users.navTimeTracking'), icon: Clock }]
      : []),
    { to: 'chat', label: t('users.navChat'), icon: MessageSquare },
    { to: 'personal-information', label: t('users.navPersonalInformation'), icon: User },
    { to: 'address', label: t('users.navAddress'), icon: MapPin },
    { to: 'contact', label: t('users.navContact'), icon: Phone },
    { to: 'documents', label: t('users.navDocuments'), icon: FolderOpen },
  ]

  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['user', id],
    queryFn: () => userService.getUserById(id!),
    enabled: !!id,
  })

  const fullName = user ? `${user.firstName} ${user.lastName}` : t('users.employeeFallback')

  return (
    <PageLayout
      isLoading={isLoading}
      error={error}
      loadingMessage={t('loading.employee')}
      errorMessage={t('errors.loadEmployee')}
    >
      <Container>
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-6'>
          <aside className='lg:col-span-3'>
            <div className={pagePanel}>
              <div className='p-5 border-b border-primary-100 bg-surface-secondary'>
                <div className='flex items-center gap-3'>
                  <div className='w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center text-white font-semibold text-base flex-shrink-0'>
                    {user?.firstName?.charAt(0) || 'M'}
                    {user?.lastName?.charAt(0) || ''}
                  </div>
                  <div className='min-w-0'>
                    <div className='font-semibold text-neutral-900 truncate'>{fullName}</div>
                    <div className='text-sm text-neutral-500 truncate'>
                      {user?.function || t('common.dash')}
                    </div>
                  </div>
                </div>
                {user && (
                  <div className='mt-3 flex items-center gap-2'>
                    <Badge
                      title={user.isActive ? t('common.active') : t('common.inactive')}
                      variant={user.isActive ? 'success' : 'error'}
                    />
                    {user.startDate && (
                      <span className='text-xs text-neutral-400'>
                        {t('common.since', { date: formatDate(user.startDate) })}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <nav className='p-2'>
                {navItems.map(item => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                      }`
                    }
                  >
                    <item.icon className='w-4 h-4 flex-shrink-0' />
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          </aside>

          <section className='lg:col-span-9'>
            <Outlet />
          </section>
        </div>
      </Container>
    </PageLayout>
  )
}
